import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

const API_BASE = "https://api.farmfox.co/farmfox/supplier/lot";
const ORANGE = "#ff4d1f";

export default function LotDetails({ lotNo: lotNoProp }) {
  const [searchParams] = useSearchParams();
  const lotNoFromUrl = searchParams.get("lotNo") || "";

  const initialLotNo = useMemo(() => {
    return (lotNoProp || lotNoFromUrl || "").toUpperCase().slice(0, 3);
  }, [lotNoProp, lotNoFromUrl]);

  const [chars, setChars] = useState(
    Array.from({ length: 3 }, (_, i) => initialLotNo[i] || "")
  );
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const inputRefs = useRef([]);

  const joinedLotNo = chars.join("").toUpperCase();

  const handleChange = (value, index) => {
    const val = value.toUpperCase().slice(0, 1);
    const next = [...chars];
    next[index] = val;
    setChars(next);

    if (val && index < 2) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && !chars[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const fetchLotDetails = async (lotNumber) => {
    const lot = lotNumber.toUpperCase().trim();

    if (lot.length !== 3) {
      setError("Enter all 3 characters");
      setData(null);
      return;
    }

    setLoading(true);
    setError("");
    setData(null);

    try {
      const response = await fetch(`${API_BASE}?lotNo=${encodeURIComponent(lot)}`);

      if (!response.ok) {
        throw new Error("Lot not found");
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError("Invalid Lot Number");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialLotNo.length === 3) {
      fetchLotDetails(initialLotNo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h2 style={styles.brand}>farmfox.co</h2>
        <h3 style={styles.title}>Code Verification</h3>
        <p style={styles.subtitle}>
          Enter batch number printed on the pack
        </p>

        <div style={styles.inputRow}>
          {chars.map((char, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              id={`box-${index}`}
              maxLength="1"
              value={char}
              style={styles.box}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              aria-label={`Lot character ${index + 1}`}
            />
          ))}
        </div>

        <button
          style={styles.button}
          onClick={() => fetchLotDetails(joinedLotNo)}
          disabled={loading}
        >
          {loading ? "PROCESSING..." : "VERIFY AND PROCESS"}
        </button>

        {loading && <p style={styles.status}>Processing...</p>}
        {error && <p style={styles.error}>{error}</p>}

        {data && (
          <div style={styles.card}>
            <p style={styles.cardLabel}>MARKETED BY:</p>
            <p style={styles.cardValue}><b>{data.companyName}</b></p>
            <p style={styles.cardText}>{data.companyAddress}</p>
            <p style={styles.cardText}><b>lot number: </b>{data.lotNo}</p>
            <p style={styles.cardText}><b>mfg Date: </b>{data.mfgDate}</p>
            <p style={styles.cardText}><b>manufactured by: </b>{data.manufacturedBy}</p>
            <p style={styles.cardText}><b>manufacturer address: </b>{data.manufacturerAddress}</p>
            <p style={styles.cardText}><b>fssai license no: </b>{data.fssaiLicenseNo}</p>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    fontFamily: "Arial, sans-serif",
    backgroundColor: "#f4f6f8",
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
  },
  container: {
    width: "100%",
    maxWidth: "420px",
    padding: "25px",
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
    textAlign: "center",
  },
  brand: {
    color: ORANGE,
    fontWeight: "bold",
    marginBottom: "10px",
  },
  title: { margin: "0 0 8px" },
  subtitle: { margin: "0 0 16px", color: "#4b5563", lineHeight: 1.5 },
  inputRow: {
    display: "flex",
    justifyContent: "center",
    gap: "8px",
    marginTop: "15px",
    flexWrap: "wrap",
  },
  box: {
    width: "42px",
    height: "42px",
    fontSize: "18px",
    textAlign: "center",
    borderRadius: "6px",
    border: `2px solid ${ORANGE}`,
    outline: "none",
    textTransform: "uppercase",
  },
  button: {
    marginTop: "20px",
    width: "100%",
    padding: "12px",
    backgroundColor: ORANGE,
    color: "white",
    border: "none",
    fontSize: "15px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 700,
  },
  status: { marginTop: "10px", color: "#374151" },
  card: {
    marginTop: "20px",
    padding: "15px",
    border: `2px solid ${ORANGE}`,
    borderRadius: "10px",
    textAlign: "left",
    background: "#fff",
  },
  cardLabel: { margin: "0 0 8px", fontWeight: 700 },
  cardValue: { margin: "0 0 8px" },
  cardText: { margin: "0 0 8px", wordBreak: "break-word" },
  error: { color: "red", marginTop: "10px", fontWeight: 600 },
};